<template>
    <div class="pr-page">

        <!-- Header -->
        <div class="pr-header">
            <div class="pr-header-left">
                <h2 class="pr-title">Project Reports</h2>
                <p class="pr-subtitle">Task progress overview across all projects</p>
            </div>
            <div class="pr-header-right">
                <div class="pr-search-wrap">
                    <i class="fas fa-search pr-search-icon"></i>
                    <input v-model="search" class="pr-search-input" placeholder="Search projects..." />
                </div>
                <select v-model="statusFilter" class="pr-filter-sel">
                    <option value="">All Health</option>
                    <option value="Done">Done</option>
                    <option value="On Track">On Track</option>
                    <option value="Behind">Behind</option>
                    <option value="At Risk">At Risk</option>
                </select>
                <button class="pr-print-btn" @click="printReport">
                    <i class="fas fa-print"></i> Print / PDF
                </button>
            </div>
        </div>

        <!-- Summary Cards -->
        <div class="pr-cards">
            <div class="pr-card">
                <div class="pr-card-icon" style="background:#eff6ff;color:#3b82f6"><i class="fas fa-folder-open"></i></div>
                <div>
                    <div class="pr-card-val">{{ projectGroups.length }}</div>
                    <div class="pr-card-lbl">Total Projects</div>
                </div>
            </div>
            <div class="pr-card">
                <div class="pr-card-icon" style="background:#f0fdf4;color:#16a34a"><i class="fas fa-tasks"></i></div>
                <div>
                    <div class="pr-card-val">{{ totalTasks }}</div>
                    <div class="pr-card-lbl">Total Tasks</div>
                </div>
            </div>
            <div class="pr-card">
                <div class="pr-card-icon" style="background:#dcfce7;color:#15803d"><i class="fas fa-check-circle"></i></div>
                <div>
                    <div class="pr-card-val">{{ totalCompleted }}</div>
                    <div class="pr-card-lbl">Completed</div>
                </div>
            </div>
            <div class="pr-card">
                <div class="pr-card-icon" style="background:#fefce8;color:#ca8a04"><i class="fas fa-spinner"></i></div>
                <div>
                    <div class="pr-card-val">{{ totalInProgress }}</div>
                    <div class="pr-card-lbl">In Progress</div>
                </div>
            </div>
            <div class="pr-card">
                <div class="pr-card-icon" style="background:#fef2f2;color:#ef4444"><i class="fas fa-exclamation-circle"></i></div>
                <div>
                    <div class="pr-card-val">{{ totalOverdue }}</div>
                    <div class="pr-card-lbl">Overdue</div>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loadingData" class="pr-loading">
            <i class="fas fa-circle-notch fa-spin"></i> Loading project data…
        </div>

        <!-- Table -->
        <div v-else class="pr-table-wrap">
            <table class="pr-table">
                <thead>
                    <tr>
                        <th style="width:36px"></th>
                        <th>Project</th>
                        <th>Tasks</th>
                        <th style="min-width:160px">Completion</th>
                        <th>Overdue</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Health</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <template v-for="pg in filteredGroups">
                    <tbody :key="'main-' + pg.id">
                        <tr class="pr-row" :class="{ 'pr-row-open': expandedRows.includes(pg.id) }">
                            <td>
                                <button class="pr-expand-btn" @click="toggleExpand(pg.id)">
                                    <i :class="expandedRows.includes(pg.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                                </button>
                            </td>
                            <td>
                                <div class="pr-proj-name">{{ pg.name }}</div>
                            </td>
                            <td>
                                <div class="pr-task-counts">
                                    <span class="pr-tc pr-tc-total">{{ pg.total }}</span>
                                    <span class="pr-tc pr-tc-done" title="Completed">✓ {{ pg.completed }}</span>
                                    <span class="pr-tc pr-tc-wip" title="In Progress">⧗ {{ pg.inProgress }}</span>
                                    <span class="pr-tc pr-tc-pend" title="Pending">◷ {{ pg.pending }}</span>
                                </div>
                            </td>
                            <td>
                                <div class="pr-progress-row">
                                    <div class="pr-progress-track">
                                        <div class="pr-progress-fill" :style="{ width: pg.completionPct + '%', background: progressColor(pg.completionPct) }"></div>
                                    </div>
                                    <span class="pr-pct-lbl">{{ pg.completionPct }}%</span>
                                </div>
                            </td>
                            <td>
                                <span v-if="pg.overdue > 0" class="pr-badge pr-badge-danger">{{ pg.overdue }}</span>
                                <span v-else class="pr-badge pr-badge-ok">0</span>
                            </td>
                            <td class="pr-date-cell">{{ pg.startDate }}</td>
                            <td class="pr-date-cell" :class="{ 'pr-date-over': pg.isOverdue }">{{ pg.endDate }}</td>
                            <td>
                                <span class="pr-health-badge" :class="healthClass(pg.health)">{{ pg.health }}</span>
                            </td>
                            <td>
                                <button class="pr-view-btn" @click="viewProject(pg.id)">
                                    <i class="fas fa-eye"></i> View
                                </button>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-if="expandedRows.includes(pg.id)" :key="'exp-' + pg.id" class="pr-expand-body">
                        <tr>
                            <td colspan="9" class="pr-expand-td">
                                <div class="pr-expand-inner">
                                    <div class="pr-expand-heading">
                                        <i class="fas fa-list-ul"></i>
                                        Tasks in <strong>{{ pg.name }}</strong>
                                        <span class="pr-expand-count">{{ pg.tasks.length }} tasks</span>
                                    </div>
                                    <table class="pr-sub-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Task Name</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Assigned To</th>
                                                <th>Start</th>
                                                <th>End</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(task, ti) in pg.tasks" :key="task.id" :class="{ 'pr-sub-overdue': isOverdueTask(task) }">
                                                <td>{{ ti + 1 }}</td>
                                                <td><span class="pr-task-name">{{ task.name }}</span></td>
                                                <td>
                                                    <span class="pr-type-chip" :class="task.taskId ? 'pr-chip-sub' : 'pr-chip-task'">
                                                        {{ task.taskId ? 'Subtask' : 'Task' }}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span class="pr-status-pill" :style="statusPillStyle(task.Status && task.Status.name)">
                                                        {{ task.Status ? task.Status.name : '—' }}
                                                    </span>
                                                </td>
                                                <td>{{ getAssignees(task) || '—' }}</td>
                                                <td>{{ formatIndianDate(task.startDate) }}</td>
                                                <td :class="{ 'pr-date-over': isOverdueTask(task) }">
                                                    {{ formatIndianDate(task.endDate) }}
                                                    <span v-if="isOverdueTask(task)" class="pr-over-chip">Overdue</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </template>
                <tbody v-if="!loadingData && filteredGroups.length === 0">
                    <tr>
                        <td colspan="9" class="pr-empty">
                            <i class="fas fa-inbox"></i> No projects found
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { formatIndianDate } from "../../../utils/common"

export default {
    data() {
        return {
            loadingData: false,
            rawTasks: [],
            search: "",
            statusFilter: "",
            expandedRows: [],
        }
    },
    async mounted() {
        await this.fetchData()
    },
    computed: {
        projectGroups() {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const map = {}

            for (const task of this.rawTasks) {
                const proj = task.Project
                if (!proj) continue
                if (!map[proj.id]) {
                    map[proj.id] = { id: proj.id, name: proj.name, tasks: [], startDate: null, endDate: null }
                }
                map[proj.id].tasks.push(task)
                if (task.startDate) {
                    const d = new Date(task.startDate)
                    if (!map[proj.id].startDate || d < map[proj.id].startDate) map[proj.id].startDate = d
                }
                if (task.endDate) {
                    const d = new Date(task.endDate)
                    if (!map[proj.id].endDate || d > map[proj.id].endDate) map[proj.id].endDate = d
                }
            }

            return Object.values(map).map((pg) => {
                const total = pg.tasks.length
                const completed = pg.tasks.filter((t) => this.isCompleted(t)).length
                const inProgress = pg.tasks.filter((t) => this.isInProgress(t)).length
                const pending = total - completed - inProgress
                const overdue = pg.tasks.filter((t) => this.isOverdueTask(t)).length
                const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0
                const isOverdue = pg.endDate && pg.endDate < today && completed < total

                let timelinePct = 0
                if (pg.startDate && pg.endDate) {
                    const totalMs = pg.endDate - pg.startDate
                    const elapsedMs = today - pg.startDate
                    timelinePct = totalMs > 0 ? Math.max(0, Math.min(100, Math.round((elapsedMs / totalMs) * 100))) : 0
                }

                let health = "On Track"
                if (completed === total && total > 0) health = "Done"
                else if (overdue > 0 || (isOverdue && completionPct < 80)) health = "At Risk"
                else if (completionPct < timelinePct - 15) health = "Behind"

                return {
                    ...pg,
                    total, completed, inProgress, pending, overdue, completionPct, isOverdue, health,
                    startDate: pg.startDate ? formatIndianDate(pg.startDate.toISOString()) : "—",
                    endDate: pg.endDate ? formatIndianDate(pg.endDate.toISOString()) : "—",
                }
            })
        },
        filteredGroups() {
            let list = this.projectGroups
            if (this.search.trim()) {
                const q = this.search.trim().toLowerCase()
                list = list.filter((pg) => pg.name.toLowerCase().includes(q))
            }
            if (this.statusFilter) {
                list = list.filter((pg) => pg.health === this.statusFilter)
            }
            return list
        },
        totalTasks()     { return this.projectGroups.reduce((s, pg) => s + pg.total, 0) },
        totalCompleted() { return this.projectGroups.reduce((s, pg) => s + pg.completed, 0) },
        totalInProgress(){ return this.projectGroups.reduce((s, pg) => s + pg.inProgress, 0) },
        totalOverdue()   { return this.projectGroups.reduce((s, pg) => s + pg.overdue, 0) },
    },
    methods: {
        formatIndianDate,
        async fetchData() {
            try {
                this.loadingData = true
                const { data } = await http.get("/project-tasks")
                this.rawTasks = data.result || []
            } catch (err) {
                console.error("Error fetching project data:", err)
            } finally {
                this.loadingData = false
            }
        },
        isCompleted(task) {
            const s = (task.Status?.name || "").toLowerCase()
            return s.includes("complet") || s === "done" || s === "closed"
        },
        isInProgress(task) {
            const s = (task.Status?.name || "").toLowerCase()
            return s.includes("progress") || s.includes("ongoing") || s.includes("active") || s.includes("review")
        },
        isOverdueTask(task) {
            if (!task.endDate || this.isCompleted(task)) return false
            const today = new Date(); today.setHours(0,0,0,0)
            return new Date(task.endDate) < today
        },
        getAssignees(task) {
            const src = task.Reporter || task.Assignees || []
            return src.map((u) => u.firstName || u.username || "").filter(Boolean).join(", ")
        },
        toggleExpand(id) {
            const idx = this.expandedRows.indexOf(id)
            if (idx === -1) this.expandedRows.push(id)
            else this.expandedRows.splice(idx, 1)
        },
        progressColor(pct) {
            if (pct >= 80) return "#22c55e"
            if (pct >= 50) return "#f59e0b"
            return "#ef4444"
        },
        healthClass(h) {
            if (h === "Done")     return "pr-health-done"
            if (h === "On Track") return "pr-health-ok"
            if (h === "Behind")   return "pr-health-behind"
            return "pr-health-risk"
        },
        statusPillStyle(name) {
            if (!name) return {}
            const n = name.toLowerCase()
            if (n.includes("complet") || n === "done" || n === "closed")
                return { background: "#dcfce7", color: "#15803d" }
            if (n.includes("progress") || n.includes("active") || n.includes("ongoing"))
                return { background: "#dbeafe", color: "#1d4ed8" }
            if (n.includes("block") || n.includes("hold"))
                return { background: "#fee2e2", color: "#b91c1c" }
            return { background: "#f1f5f9", color: "#475569" }
        },
        viewProject(id) {
            this.$router.push(`/project-report-detail/${id}`)
        },
        printReport() { window.print() },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.pr-page { padding: 20px; max-width: 1400px; }

.pr-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    flex-wrap: wrap; gap: 12px; margin-bottom: 24px;
}
.pr-title { font-size: 20px; font-weight: 700; color: $gray800; margin: 0; }
.pr-subtitle { font-size: 13px; color: $gray500; margin: 3px 0 0; }
.pr-header-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.pr-search-wrap { position: relative; display: flex; align-items: center; }
.pr-search-icon { position: absolute; left: 10px; color: $gray400; font-size: 13px; pointer-events: none; }
.pr-search-input {
    padding: 8px 12px 8px 32px; border: 1px solid $gray200; border-radius: $radiusSm;
    font-size: 13px; width: 200px; outline: none;
    &:focus { border-color: $primaryColor; }
}
.pr-filter-sel {
    padding: 8px 10px; border: 1px solid $gray200; border-radius: $radiusSm;
    font-size: 13px; background: white; color: $gray700; outline: none; cursor: pointer;
    &:focus { border-color: $primaryColor; }
}
.pr-print-btn {
    display: flex; align-items: center; gap: 6px; padding: 8px 14px;
    background: $primaryColor; color: white; border: none; border-radius: $radiusSm;
    font-size: 13px; font-weight: 500; cursor: pointer; transition: opacity 0.15s;
    &:hover { opacity: 0.88; }
}

.pr-cards {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; margin-bottom: 24px;
}
@media (max-width: 900px) { .pr-cards { grid-template-columns: repeat(2, 1fr); } }

.pr-card {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    padding: 16px; display: flex; align-items: center; gap: 14px; box-shadow: $shadowSm;
}
.pr-card-icon {
    width: 42px; height: 42px; border-radius: $radiusSm;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
}
.pr-card-val { font-size: 22px; font-weight: 700; color: $gray800; line-height: 1; }
.pr-card-lbl { font-size: 12px; color: $gray500; margin-top: 3px; }

.pr-loading { padding: 48px; text-align: center; color: $gray400; font-size: 15px; i { margin-right: 8px; } }

.pr-table-wrap {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    overflow: hidden; box-shadow: $shadowSm; overflow-x: auto;
}
.pr-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
    thead tr { background: $gray50; border-bottom: 1px solid $gray200; }
    th {
        padding: 10px 14px; text-align: left; font-weight: 600; font-size: 12px;
        color: $gray500; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em;
    }
    td { padding: 11px 14px; border-bottom: 1px solid $gray100; vertical-align: middle; }
}
.pr-row { transition: background 0.1s; &:hover { background: $gray50; } }
.pr-row-open { background: #f0f9ff; }

.pr-expand-btn {
    width: 24px; height: 24px; border: 1px solid $gray200; border-radius: $radiusSm;
    background: white; color: $gray500; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; transition: all 0.15s;
    &:hover { background: $gray100; color: $gray700; }
}

.pr-proj-name { font-weight: 600; color: $gray800; font-size: 13px; }

.pr-task-counts { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.pr-tc { font-size: 12px; font-weight: 500; padding: 2px 6px; border-radius: $radiusSm; }
.pr-tc-total { background: $gray100; color: $gray700; font-weight: 700; }
.pr-tc-done  { background: #dcfce7; color: #16a34a; }
.pr-tc-wip   { background: #fef9c3; color: #ca8a04; }
.pr-tc-pend  { background: #f1f5f9; color: $gray500; }

.pr-progress-row { display: flex; align-items: center; gap: 8px; }
.pr-progress-track { flex: 1; height: 7px; background: $gray100; border-radius: 999px; overflow: hidden; }
.pr-progress-fill { height: 100%; border-radius: 999px; transition: width 0.4s ease; }
.pr-pct-lbl { font-size: 12px; font-weight: 600; color: $gray600; min-width: 34px; text-align: right; }

.pr-badge { display: inline-block; padding: 2px 8px; border-radius: $radiusFull; font-size: 12px; font-weight: 600; }
.pr-badge-danger { background: #fee2e2; color: #dc2626; }
.pr-badge-ok     { background: #dcfce7; color: #16a34a; }

.pr-status-pill { display: inline-block; padding: 3px 10px; border-radius: $radiusFull; font-size: 11px; font-weight: 500; white-space: nowrap; }
.pr-date-cell { color: $gray600; font-size: 12px; white-space: nowrap; }
.pr-date-over { color: #dc2626 !important; font-weight: 600; }

.pr-health-badge { display: inline-block; padding: 3px 10px; border-radius: $radiusFull; font-size: 11px; font-weight: 600; white-space: nowrap; }
.pr-health-done   { background: #dcfce7; color: #15803d; }
.pr-health-ok     { background: #dbeafe; color: #1d4ed8; }
.pr-health-behind { background: #fef9c3; color: #b45309; }
.pr-health-risk   { background: #fee2e2; color: #dc2626; }

.pr-view-btn {
    display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px;
    background: $gray100; border: none; border-radius: $radiusSm;
    font-size: 12px; color: $gray700; cursor: pointer; font-weight: 500;
    transition: background 0.15s; white-space: nowrap;
    &:hover { background: $gray200; }
}

.pr-expand-td { padding: 0 !important; background: #f8fafc; border-left: 3px solid #3b82f6; }
.pr-expand-inner { padding: 16px 20px; }
.pr-expand-heading {
    font-size: 13px; font-weight: 600; color: $gray700; margin-bottom: 12px;
    display: flex; align-items: center; gap: 8px;
    i { color: #3b82f6; }
}
.pr-expand-count {
    margin-left: auto; font-size: 11px; font-weight: 500; color: $gray500;
    background: $gray100; padding: 2px 8px; border-radius: $radiusFull;
}

.pr-sub-table {
    width: 100%; border-collapse: collapse; font-size: 12px;
    thead tr { background: #e2e8f0; }
    th { padding: 7px 12px; font-weight: 600; color: $gray600; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; }
    td { padding: 8px 12px; border-bottom: 1px solid $gray100; color: $gray700; vertical-align: middle; }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #f1f5f9; }
}
.pr-sub-overdue td { background: #fff5f5 !important; }
.pr-task-name { font-weight: 500; color: $gray800; }

.pr-type-chip { display: inline-block; padding: 2px 7px; border-radius: $radiusFull; font-size: 10px; font-weight: 600; }
.pr-chip-task { background: #dbeafe; color: #1d4ed8; }
.pr-chip-sub  { background: #f3e8ff; color: #7c3aed; }

.pr-over-chip {
    display: inline-block; margin-left: 5px; padding: 1px 6px;
    background: #fee2e2; color: #dc2626; border-radius: $radiusFull;
    font-size: 10px; font-weight: 600;
}

.pr-empty { text-align: center; padding: 48px; color: $gray400; font-size: 14px; i { display: block; font-size: 28px; margin-bottom: 8px; } }
</style>

<style>
@media print {
    body * { visibility: hidden !important; }
    .pr-page, .pr-page * { visibility: visible !important; }
    .pr-page { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
    .pr-print-btn, .pr-expand-btn, .pr-view-btn, .pr-search-wrap, .pr-filter-sel { display: none !important; }
    .pr-table-wrap { box-shadow: none; border: 1px solid #ccc; }
    .pr-progress-fill, .pr-health-badge, .pr-badge, .pr-status-pill, .pr-type-chip, .pr-tc { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { size: A4 landscape; margin: 15mm; }
}
</style>
